import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import {
  blackoutPeriodsQuery,
  createBlackoutPeriodMutation,
  deleteBlackoutPeriodMutation,
  updateBlackoutPeriodMutation,
} from "./queries";

const TOAST_MS = 3000;

const nullableTrim = (value) => value?.trim() || null;

function periodFields(period) {
  return {
    name: period.name.trim(),
    reason: nullableTrim(period.reason),
    startsAt: period.startsAt || null,
    endsAt: period.endsAt || null,
    startTime: period.startTime,
    endTime: period.endTime,
    isActive: period.isActive,
  };
}

export default function useBlackoutPeriods(installationId, includeInactive = true) {
  const [toast, setToast] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const timer = useRef(null);
  const variables = { installationId, includeInactive };
  const { data, loading, error, refetch } = useQuery(blackoutPeriodsQuery, {
    variables,
    skip: !installationId,
    fetchPolicy: includeInactive ? "no-cache" : "cache-and-network",
  });
  const [createPeriod, createState] = useMutation(createBlackoutPeriodMutation);
  const [updatePeriod, updateState] = useMutation(updateBlackoutPeriodMutation);
  const [deletePeriod, deleteState] = useMutation(deleteBlackoutPeriodMutation);
  const activePeriodsRefetch = [{
    query: blackoutPeriodsQuery,
    variables: { installationId, includeInactive: false },
  }];

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (message) => {
    setToast(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), TOAST_MS);
  };
  const run = async (mutate, message) => {
    setMutationError(null);
    try {
      await mutate();
      await refetch(variables);
      flash(message);
      return true;
    } catch (err) {
      setMutationError(err);
      return false;
    }
  };

  return {
    periods: data?.blackoutPeriods ?? [],
    loading,
    error,
    mutationError,
    mutationInFlight: createState.loading || updateState.loading || deleteState.loading,
    toast,
    create: (period) => run(
      () => createPeriod({
        variables: { period: { installationId, ...periodFields(period) } },
        refetchQueries: activePeriodsRefetch,
      }),
      `${period.name.trim()} added.`,
    ),
    update: (period) => run(
      () => updatePeriod({
        variables: { period: { id: period.id, ...periodFields(period) } },
        refetchQueries: activePeriodsRefetch,
      }),
      `${period.name.trim()} updated.`,
    ),
    remove: (period) => run(
      () => deletePeriod({
        variables: { id: period.id },
        refetchQueries: activePeriodsRefetch,
      }),
      `${period.name} removed.`,
    ),
  };
}
